/* WENNEBIRD R3: latest-request isolation, bounded fetches and exact quantity rules. */
if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        if (this._wbConnected) return;
        this._wbConnected = true;
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this._wbConnected = false;
        this._wbRequestId = (this._wbRequestId || 0) + 1;
        this.abortController?.abort();
        this._wbQuantityAbort?.abort();
        clearTimeout(this._wbRequestTimeout);
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;

        if (shouldSwapProduct) {
          const nextUrl = new URL(productUrl, window.location.origin);
          if (nextUrl.origin !== window.location.origin) return;

          nextUrl.searchParams.delete('variant');
          nextUrl.searchParams.delete('option_values');
          nextUrl.searchParams.delete('section_id');
          for (const key of ['vehicle_year', 'vehicle_make', 'vehicle_model', 'wb_vehicle']) {
            nextUrl.searchParams.delete(key);
          }

          const optionValues = Array.isArray(selectedOptionValues) ? selectedOptionValues.filter(Boolean) : [];
          if (optionValues.length) nextUrl.searchParams.set('option_values', optionValues.join(','));

          const current = new URLSearchParams(window.location.search);
          for (const key of ['preview_theme_id', 'pb']) {
            if (current.has(key)) nextUrl.searchParams.set(key, current.get(key));
          }

          window.location.assign(nextUrl.toString());
          return;
        }

        this.pendingRequestUrl = productUrl;
        const localVariant = this.getLocalSelectedVariant();

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            false,
            localVariant?.id
          ),
          targetId: target.id,
          callback: this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        clearTimeout(this._wbRequestTimeout);
        const controller = new AbortController();
        this.abortController = controller;
        const requestId = this._wbRequestId = (this._wbRequestId || 0) + 1;
        let timedOut = false;
        this._wbRequestTimeout = setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, 15000);
        const current = () => this.isConnected && this._wbRequestId === requestId;
        const fetchMarkup = async (url) => {
          const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Product information could not load (' + response.status + ')');
          const responseText = await response.text();
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          if (!html.querySelector('product-info')) throw new Error('Incomplete product response');
          return html;
        };
        const fallbackUrl = (() => {
          try {
            const url = new URL(requestUrl, window.location.origin);
            if (!url.searchParams.has('section_id')) return null;
            url.searchParams.delete('section_id');
            return url.pathname + url.search;
          } catch {
            return null;
          }
        })();

        this.dataset.wbVariantPending = 'true';
        this.dataset.wbVariantError = 'false';
        this.dispatchEvent(new CustomEvent('wb:variant-start', { bubbles: true }));
        fetchMarkup(requestUrl)
          .catch((error) => {
            if (controller.signal.aborted || !fallbackUrl) throw error;
            console.warn('WENNEBIRD section variant update failed; retrying full product markup.', error);
            return fetchMarkup(fallbackUrl);
          })
          .then((html) => {
            if (!current()) return;
            this.pendingRequestUrl = null;
            callback(html);
            if (current()) {
              this.dataset.wbVariantPending = 'false';
              this.dispatchEvent(new CustomEvent('wb:variant-ready', { bubbles: true }));
              if (targetId) this.querySelector('#' + CSS.escape(targetId))?.focus({ preventScroll: true });
            }
          })
          .catch((error) => {
            if (!current() || (error.name === 'AbortError' && !timedOut)) return;
            this.pendingRequestUrl = null;
            this.dataset.wbVariantPending = 'false';
            this.dataset.wbVariantError = 'true';
            this.dispatchEvent(new CustomEvent('wb:variant-error', { bubbles: true, detail: { timedOut } }));
            this.productForm?.handleErrorMessage(
              timedOut
                ? 'The selected configuration is taking too long to load. Please select it again.'
                : 'The selected configuration could not load. Please try selecting it again.'
            );
            console.error('WENNEBIRD variant update failed', error);
          })
          .finally(() => {
            if (this._wbRequestId === requestId) clearTimeout(this._wbRequestTimeout);
          });
      }

      getSelectedVariant(productInfoNode) {
        const text = productInfoNode?.querySelector('variant-selects [data-selected-variant]')?.textContent;
        if (!text) return null;
        try { return JSON.parse(text); } catch { return null; }
      }

      getLocalSelectedVariant() {
        const source = this.querySelector('[data-wb-product-variants]');
        const selectors = this.variantSelectors;
        if (!source || !selectors) return null;

        let variants = [];
        try {
          variants = JSON.parse(source.textContent || '[]');
        } catch {
          return null;
        }
        if (!Array.isArray(variants) || !variants.length) return null;

        const values = Array.from(
          selectors.querySelectorAll('select[name^="options["], input[type="radio"][name]:checked')
        ).map((control) => control.tagName === 'SELECT' ? control.value : control.value);

        if (!values.length) return null;

        return variants.find((variant) => {
          const options = Array.isArray(variant.options)
            ? variant.options
            : [variant.option1, variant.option2, variant.option3].filter((value) => value != null);
          return options.length === values.length && options.every((value, index) => String(value) === String(values[index]));
        }) || null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false, variantId = null) {
        const result = new URL(url, window.location.origin);
        if (result.origin !== window.location.origin) throw new Error('Unexpected product origin');
        result.searchParams.delete('variant');
        result.searchParams.delete('option_values');
        result.searchParams.delete('section_id');
        if (!shouldFetchFullPage) result.searchParams.set('section_id', this.sectionId);
        if (variantId) result.searchParams.set('variant', String(variantId));
        else if (Array.isArray(optionValues) && optionValues.length) result.searchParams.set('option_values', optionValues.join(','));
        const current = new URLSearchParams(window.location.search);
        for (const key of ['vehicle_year', 'vehicle_make', 'vehicle_model', 'wb_vehicle', 'preview_theme_id', 'pb']) {
          if (current.has(key)) result.searchParams.set(key, current.get(key));
        }
        return result.pathname + result.search;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        const current = this.variantSelectors;
        if (!variantSelects || !current) return;
        // Keep native form controls mounted so app variant listeners retain their bindings.
        const nativeControls = 'select[name^="options["], input[type="radio"][data-option-value-id]';
        const sources = Array.from(variantSelects.querySelectorAll(nativeControls));
        const destinations = Array.from(current.querySelectorAll(nativeControls));
        if (sources.length !== destinations.length || sources.some((source, index) => source.id !== destinations[index]?.id)) {
          this.preProcessHtmlCallbacks.forEach((callback) => callback(variantSelects));
          current.replaceWith(variantSelects.cloneNode(true));
          return;
        }
        sources.forEach((source, index) => {
          const destination = destinations[index];
          if (source.tagName === 'SELECT') {
            destination.innerHTML = source.innerHTML;
            destination.value = source.value;
          } else {
            destination.checked = source.checked;
            destination.toggleAttribute('checked', source.checked);
            destination.disabled = source.disabled;
            destination.className = source.className;
            Array.from(source.attributes).filter(({ name }) => name.startsWith('data-')).forEach(({ name, value }) => destination.setAttribute(name, value));
          }
        });
        const selected = current.querySelector('[data-selected-variant]');
        const nextSelected = variantSelects.querySelector('[data-selected-variant]');
        if (selected && nextSelected) selected.textContent = nextSelected.textContent;
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          // Refresh current stock and media records from the authoritative response.
          for (const selector of ['[data-wb-product-variants]', '[data-wb-variant-media]', '[data-delivery-variants]']) {
            const source = html.querySelector(selector);
            const destination = this.querySelector(selector);
            if (source && destination) destination.textContent = source.textContent;
          }
          delete this._wbVariantMedia;
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId: this.sectionId, html, variant: null } });
            return;
          }

          this.updateMedia(html, html.querySelector('media-gallery')?.dataset.wbFeaturedMediaId || variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          const sourceSubmit = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const sourceSubmitText =
            sourceSubmit?.querySelector('span')?.textContent?.trim() ||
            (sourceSubmit?.hasAttribute('disabled')
              ? (window.variantStrings?.soldOut || 'Sold out')
              : (window.variantStrings?.addToCart || 'Add to Cart'));
          this.productForm?.toggleSubmitButton(
            sourceSubmit?.hasAttribute('disabled') ?? true,
            sourceSubmitText
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          input.setAttribute('value', input.value);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const nextUrl = new URL(url, window.location.origin);
        if (nextUrl.origin !== window.location.origin) return;
        nextUrl.searchParams.delete('option_values');
        nextUrl.searchParams.delete('section_id');
        if (variantId) nextUrl.searchParams.set('variant', variantId);
        else nextUrl.searchParams.delete('variant');
        if (this.dataset.updateUrl !== 'false') {
          const current = new URLSearchParams(window.location.search);
          for (const key of ['vehicle_year', 'vehicle_make', 'vehicle_model', 'wb_vehicle', 'preview_theme_id', 'pb']) {
            if (current.has(key)) nextUrl.searchParams.set(key, current.get(key));
          }
          window.history.replaceState(window.history.state, '', nextUrl.pathname + nextUrl.search);
        }
        this.querySelector('share-button')?.updateUrl(nextUrl.toString());
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings?.unavailable || 'Unavailable');

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        this.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        if (!this.quantityInput) return;
        const input = this.quantityInput;
        const readInteger = (value, fallback) => value !== '' && Number.isInteger(Number(value)) ? Number(value) : fallback;
        const cartQuantity = Math.max(0, readInteger(input.dataset.cartQuantity, 0));
        const minimum = Math.max(1, readInteger(input.dataset.min, 1));
        const step = Math.max(1, readInteger(input.getAttribute('step'), 1));
        const hasMaximum = input.hasAttribute('data-max') && input.dataset.max !== '';
        const totalMaximum = hasMaximum ? readInteger(input.dataset.max, null) : null;
        const maximum = totalMaximum === null ? null : Math.max(0, totalMaximum - cartQuantity);
        const min = cartQuantity >= minimum ? step : Math.max(1, minimum - cartQuantity);
        input.min = String(min);
        input.step = String(step);
        if (maximum === null) input.removeAttribute('max');
        else input.max = String(maximum); // Zero is a real limit, not an absent limit.
        const impossible = maximum !== null && maximum < min;
        this.dataset.wbQuantityBlocked = String(impossible);
        input.setCustomValidity(impossible ? 'The maximum quantity for this item is already in your cart.' : '');
        input.value = String(min);
        if (impossible) this.productForm?.toggleSubmitButton(true);
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
        this.dispatchEvent(new CustomEvent('wb:quantity-rules-updated', { bubbles: true }));
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId || !this.quantityInput) return;
        this._wbQuantityAbort?.abort();
        const controller = this._wbQuantityAbort = new AbortController();
        const timer = setTimeout(() => controller.abort(), 15000);
        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        spinner?.classList.remove('hidden');
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set('variant', currentVariantId);
        url.searchParams.set('section_id', this.dataset.section);
        fetch(url.pathname + url.search, { signal: controller.signal, credentials: 'same-origin' })
          .then((response) => { if (!response.ok) throw new Error('Quantity rules unavailable'); return response.text(); })
          .then((responseText) => {
            if (!this.isConnected || this._wbQuantityAbort !== controller || this.productForm?.variantIdInput?.value !== currentVariantId) return;
            this.updateQuantityRules(this.dataset.section, new DOMParser().parseFromString(responseText, 'text/html'));
          })
          .catch((error) => { if (error.name !== 'AbortError') console.error(error); })
          .finally(() => { clearTimeout(timer); if (this._wbQuantityAbort === controller) spinner?.classList.add('hidden'); });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput || !this.quantityForm) return;

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
        this.setQuantityBoundries();
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
